import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  FormField,
  FormRow,
  FormSection,
  Input,
  Text,
} from '../ui';
import { Controller, useForm } from 'react-hook-form';
import * as zod from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { nullableDateToString, stringToNumber } from '../../lib/zod-util';
import type { NorForm__NoticeOfReadinessFragment } from '../../graphql';
import { useCreateNorMutation, useUpdateNorMutation } from '../../graphql';
import { timeFieldFormat } from '../ui/TimeField';
import { toast } from 'react-toastify';

const NORSchema = zod.object({
  id: zod.number().nullable(),
  portCallId: zod.number(),
  eta: zod.date().nullable(),
  berthingProspects: zod.date().nullable(),
  etdBerth: zod.date().nullable(),
  ataBerth: zod.date().nullable(),
  ataPbp: zod.date().nullable(),
  norTendered: zod.date().nullable(),
});

const NORSaveSchema = NORSchema.extend({
  eta: nullableDateToString,
  berthingProspects: nullableDateToString,
  etdBerth: nullableDateToString,
  ataBerth: nullableDateToString,
  ataPbp: nullableDateToString,
  norTendered: nullableDateToString,
});

type NORFormFields = zod.infer<typeof NORSchema>;

export interface NORProps {
  nor?: NorForm__NoticeOfReadinessFragment;
  onSave?: () => void;
  portCallId: number;
  portName: string;
  portTimeZone: string | null;
  portPta: number | undefined;
  userId: number;
  readOnly?: boolean;
}

const NORForm = ({
  nor,
  onSave,
  portCallId,
  portName,
  portTimeZone,
  portPta,
  userId,
  readOnly,
}: NORProps): JSX.Element => {
  const [
    createNor,
    { data: createNorData, error: createNorError, loading: createNorLoading },
  ] = useCreateNorMutation();
  const [
    updateNor,
    { data: updateNorData, error: updateNorError, loading: updateNorLoading },
  ] = useUpdateNorMutation();

  const saveLoading = createNorLoading || updateNorLoading;
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<boolean>(false);

  const lastSaving = useRef<'prospects' | 'nor' | null>(null);

  useEffect(
    () => setSaveSuccess(!!createNorData || !!updateNorData),
    [createNorData, updateNorData],
  );
  useEffect(
    () => setSaveError(!!createNorError || !!updateNorError),
    [createNorError, updateNorError],
  );
  useEffect(() => {
    setSaveSuccess(false);
    setSaveError(false);
  }, [portCallId]);

  const defaultValues = useMemo<NORFormFields>(
    () => ({
      id: nor?.id ?? null,
      portCallId,
      eta: nor?.eta ? new Date(nor.eta) : null,
      berthingProspects: nor?.berthingProspects
        ? new Date(nor.berthingProspects)
        : null,
      etdBerth: nor?.etdBerth ? new Date(nor.etdBerth) : null,
      ataBerth: nor?.ataBerth ? new Date(nor.ataBerth) : null,
      ataPbp: nor?.ataPbp ? new Date(nor.ataPbp) : null,
      norTendered: nor?.norTendered ? new Date(nor.norTendered) : null,
    }),
    [portCallId, nor],
  );

  const { control, handleSubmit, register, reset } = useForm<NORFormFields>({
    resolver: zodResolver(NORSchema),
    defaultValues,
  });

  useEffect(() => reset(defaultValues), [defaultValues, reset]);

  const onFormSubmit =
    (formToSave: typeof lastSaving.current) => async (data: NORFormFields) => {
      lastSaving.current = formToSave;
      try {
        const { id, ...norData } = NORSaveSchema.parse(data);

        if (
          norData.eta &&
          norData.berthingProspects &&
          norData.eta > norData.berthingProspects
        ) {
          toast.error('Arrival needs to be before berthing');
          return;
        }

        if (
          norData.berthingProspects &&
          norData.etdBerth &&
          norData.berthingProspects > norData.etdBerth
        ) {
          toast.error('Berthing needs to be before departure');
          return;
        }

        if (typeof id === 'number') {
          await updateNor({
            variables: { input: { id, portNoticePatch: norData } },
          });
        } else {
          await createNor({
            variables: { input: { portNotice: { userId, ...norData } } },
          });
        }

        onSave?.();
      } catch (error) {
        console.error('Error saving NOR', error);
      }
    };

  return (
    <Form onSubmit={handleSubmit(onFormSubmit(null))} border="none">
      <input
        type="hidden"
        {...register('id', { setValueAs: stringToNumber })}
      />
      <input
        type="hidden"
        {...register('portCallId', { setValueAs: stringToNumber })}
      />
      <FormSection title="Prospects">
        <FormRow>
          <FormField label="Port" width={200}>
            <Input readOnly disabled value={portName} />
          </FormField>
          <FormField data-testid="PTA-field" label="PTA" width={150}>
            <Text mt={3}>
              {portPta ? timeFieldFormat(portPta, portTimeZone ?? 'UTC') : ''}
            </Text>
          </FormField>
          <FormField label="ETA" data-testid="docs-prospects-eta" width={200}>
            <Controller
              control={control}
              name="eta"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value ? value : null}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
          <FormField label="ETB" data-testid="docs-prospects-etb" width={200}>
            <Controller
              control={control}
              name="berthingProspects"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value ? value : null}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
          <FormField label="ETD" data-testid="docs-prospects-etd" width={200}>
            <Controller
              control={control}
              name="etdBerth"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value ? value : null}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
        </FormRow>
        {!readOnly && (
          <FormRow alignItems="center">
            <Button
              disabled={saveLoading}
              variant="secondary"
              alignSelf="flex-start"
              onPress={handleSubmit(onFormSubmit('prospects'))}
              mr={3}
            >
              Save Prospects
            </Button>
            {lastSaving.current !== 'nor' &&
              (saveSuccess ? (
                <Text fontWeight="bold" color="#5fad09">
                  ✓ Saved successfully
                </Text>
              ) : saveError ? (
                <Text fontWeight="bold" color="#FC4A15">
                  ✗ Error saving
                </Text>
              ) : null)}
          </FormRow>
        )}
      </FormSection>

      <FormSection title="Notice of readiness">
        <FormRow>
          <FormField label="Port" width={200}>
            <Input readOnly disabled value={portName} />
          </FormField>
          <FormField label="Notice given at" width={200}>
            <Controller
              control={control}
              name="norTendered"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value ? value : null}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
          <FormField label="End of sea passage" width={200}>
            <Controller
              control={control}
              name="ataPbp"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value ? value : null}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
          <FormField label="Berthed" width={200}>
            <Controller
              control={control}
              name="ataBerth"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value ? value : null}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
        </FormRow>
        {!readOnly && (
          <FormRow alignItems="center">
            <Button
              disabled={saveLoading}
              variant="secondary"
              alignSelf="flex-start"
              onPress={handleSubmit(onFormSubmit('nor'))}
              mr={3}
            >
              Save Notice of Readiness
            </Button>
            {lastSaving.current !== 'prospects' &&
              (saveSuccess ? (
                <Text fontWeight="bold" color="#5fad09">
                  ✓ Saved successfully
                </Text>
              ) : saveError ? (
                <Text fontWeight="bold" color="#FC4A15">
                  ✗ Error saving
                </Text>
              ) : null)}
          </FormRow>
        )}
      </FormSection>
    </Form>
  );
};

export default NORForm;
